import { FileAccess, YearMonth } from '@wilson/interfaces';
import { WorkTimeCache } from './work-time-cache.class';
import { UserPayrollTimeSheetsService } from '@wilson/user-payroll-time-sheets';
import { FilesService } from '@wilson/files';
import { inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { DateTimeFormat } from '@wilson/utils';
import { Observable, of, switchMap } from 'rxjs';

export interface PdfTimesheetData {
  fileAccess: FileAccess | null;
  fileName: string;
}

export class PdfTimesheetCache extends WorkTimeCache<
  YearMonth,
  FileAccess | null
> {
  private readonly userPayrollTimeSheetsService = inject(
    UserPayrollTimeSheetsService,
  );
  private readonly filesService = inject(FilesService);
  private readonly store = inject(Store);

  constructor() {
    super({
      dataFactory: (monthYear) => {
        const userId = this.store.selectSnapshot(AuthState.userId) as string;
        return this.getUserTimesheetWithFileAccess(userId, monthYear);
      },
    });
  }

  private getUserTimesheetWithFileAccess(
    userId: string,
    monthYear: YearMonth,
  ): Observable<FileAccess | null> {
    const start = format(
      startOfMonth(new Date(monthYear)),
      DateTimeFormat.DatabaseDateFormat,
    );
    const end = format(
      endOfMonth(new Date(monthYear)),
      DateTimeFormat.DatabaseDateFormat,
    );

    return this.userPayrollTimeSheetsService
      .getUserPayrollTimeSheets(userId, { start, end })
      .pipe(
        switchMap((userTimeSheets) => {
          if (userTimeSheets.length === 0) {
            return of(null);
          }

          const userTimeSheet = userTimeSheets[0];
          return this.filesService.getFileAccess(userTimeSheet.filePath);
        }),
      );
  }
}
